import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MapPinIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { fetchProject } from "../../../api/project";
import { ResourceLoading } from "../../utils/ResourceLoading";

const projectFields =
  "id,displayTitle,name,project_number,description,customer,deliveryAddress";

function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export function PlanEventProjectSection({ projectId, projectFromEvent = null }) {
  const { t } = useTranslation();
  const [project, setProject] = useState(projectFromEvent);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    const hasRich =
      projectFromEvent?.description ||
      projectFromEvent?.customer?.displayTitle ||
      projectFromEvent?.deliveryAddress?.full_address;
    if (hasRich) {
      setProject(projectFromEvent);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchProject(projectId, { fields: projectFields })
      .then((res) => {
        if (!cancelled) setProject(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setProject(projectFromEvent);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, projectFromEvent]);

  if (!projectId) return null;

  const label =
    project?.displayTitle ||
    project?.name ||
    (project?.project_number ? `#${project.project_number}` : null);
  const customerName =
    project?.customer?.displayTitle || project?.customer?.name;
  const address =
    project?.deliveryAddress?.full_address ||
    project?.deliveryAddress?.displayTitle;
  const desc = stripHtml(project?.description);

  return (
    <section className="rounded-lg border border-teal-100 bg-teal-50/40 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-teal-800">
            {t("calendar.plan_event.project_context_title")}
          </h3>
          {label ? (
            <p className="mt-1 text-sm font-medium text-gray-900">{label}</p>
          ) : null}
        </div>
        <Link
          to={`/dashboard/projects/${projectId}`}
          className="shrink-0 text-xs font-medium text-teal-700 hover:text-teal-900"
        >
          {t("calendar.plan_event.open_project")}
        </Link>
      </div>

      {loading ? (
        <ResourceLoading />
      ) : (
        <>
          {customerName ? (
            <div className="flex items-start gap-2 text-sm text-gray-700">
              <BuildingOffice2Icon className="h-4 w-4 shrink-0 text-gray-500 mt-0.5" />
              <span>{customerName}</span>
            </div>
          ) : null}
          {address ? (
            <div className="flex items-start gap-2 text-sm text-gray-700">
              <MapPinIcon className="h-4 w-4 shrink-0 text-gray-500 mt-0.5" />
              <span>{address}</span>
            </div>
          ) : null}
          {desc ? (
            <p className="text-sm text-gray-600 line-clamp-4">{desc}</p>
          ) : (
            <p className="text-sm italic text-gray-500">
              {t("calendar.plan_event.project_no_description")}
            </p>
          )}
        </>
      )}
    </section>
  );
}
